/**
 * Общие примитивы и data-компоненты.
 *
 * Примитивы ничего не знают про контуры и хозяйства; data-компоненты знают
 * форму серверных контрактов (эпистемика, происхождение, состояния), но не
 * выдают команд и не переопределяют смысл статусов.
 */

import type { ReactNode } from "react";
import type { ApiError, ScopeOut, SourceRef } from "../api/client";
import { failureKind } from "../api/client";
import { epistemicClass } from "../lib/epistemic";
import { formatArea, formatCount, missingLabel } from "../lib/format";
import { UNKNOWN } from "../lib/crops";
import { defineMessages } from "../i18n/define";
import { UNIT_LABELS, useT } from "../i18n/hooks";
import { tr } from "../i18n/runtime";
import { Explained, InfoPopover } from "./InfoPopover";
import styles from "./ui.module.css";

const m = defineMessages({
  absentInSource: { id: "common.kit.ui.absentInSource", defaultMessage: "Значение отсутствует в источнике", description: "Всплывающая подсказка над пометкой «нет данных» на месте числа площади" },
  exact: { id: "common.kit.ui.exact", defaultMessage: "точно: {value} {unit}", description: "Подсказка над округлённой площадью: точное значение; {value} — число, {unit} — единица («га»)" },
  unknown: { id: "common.kit.ui.unknown", defaultMessage: "неизвестно", description: "Значение текстового поля, которого нет (подпись вместо пустой строки); строчная" },
  quantityFallback: { id: "common.kit.ui.quantityFallback", defaultMessage: "величина", description: "Запасное имя KPI-карточки для экранных дикторов, когда подпись не строка; строчная" },
  notYours: { id: "common.kit.ui.notYours", defaultMessage: "Это не ваши данные", description: "Заголовок панели отказа: объект принадлежит чужому хозяйству, вне области аккаунта" },
  noRole: { id: "common.kit.ui.noRole", defaultMessage: "Роль не назначена", description: "Заголовок панели отказа: вход выполнен, но аккаунту не назначена роль" },
  signInNeeded: { id: "common.kit.ui.signInNeeded", defaultMessage: "Нужен вход", description: "Заголовок панели отказа: для просмотра или действия нужно войти" },
  scopeNarrowed: { id: "common.kit.ui.scopeNarrowed", defaultMessage: "Показаны только ваши хозяйства", description: "Заголовок плашки над списком: выдача сужена до хозяйств аккаунта (фермер); пояснение берётся с сервера" },
  axisNotApplied: { id: "common.kit.ui.axisNotApplied", defaultMessage: "Выбранная ось к этой выдаче не применена", description: "Заголовок плашки над списком: выбранный фильтр (сезон и т. п.) выдачу не сузил; причина берётся с сервера" },
  noProvenance: { id: "common.kit.ui.noProvenance", defaultMessage: "происхождение не указано", description: "Пометка вместо строки источника данных, когда происхождение не названо; строчная" },
  sheet: { id: "common.kit.ui.sheet", defaultMessage: "лист «{name}»", description: "Часть строки источника: имя листа исходной таблицы; {name} — имя листа из файла (не переводится)" },
  row: { id: "common.kit.ui.row", defaultMessage: "строка {no}", description: "Часть строки источника: номер строки исходной таблицы" },
  cells: { id: "common.kit.ui.cells", defaultMessage: "ячейки {range}", description: "Часть строки источника: диапазон ячеек исходной таблицы («B2:D9»)" },
  derivation: { id: "common.kit.ui.derivation", defaultMessage: "вывод: {method}", description: "Часть строки источника: способ, которым значение выведено из исходных данных; {method} — код метода (не переводится)" },
});

/**
 * Объяснение величины — общая точка входа для всех экранов.
 *
 * Реэкспорт, а не второй компонент: точка одна, чтобы «что входит в
 * знаменатель» везде открывалось одинаково — нажатием, с клавиатуры и на
 * сенсорном экране, а не наведением мыши. Правило: если без объяснения число
 * читается НЕВЕРНО, объяснение обязано быть здесь, а не в `title`.
 */
export { Explained, InfoPopover };

/* ------------------------------------------------------------------ */
/* Значения                                                            */
/* ------------------------------------------------------------------ */

/**
 * Площадь в гектарах. `null` показывается как «нет данных» — НИКОГДА как 0.
 * Настоящий фактический ноль (сервер прислал 0) показывается как «0,00».
 */
export function Area({
  microha,
  unit,
  rounded = false,
}: {
  microha: number | null | undefined;
  unit?: string | null;
  /** Только для крупных плашек: до сотых. Точное значение — в подсказке. */
  rounded?: boolean;
}) {
  const t = useT();
  const { text, missing } = formatArea(microha, { rounded });
  const unitText = unit === undefined ? t(UNIT_LABELS.ha) : unit;
  if (missing) {
    return (
      <span className={styles.missing} title={t(m.absentInSource)}>
        {missingLabel()}
      </span>
    );
  }
  const exact = formatArea(microha).text;
  return (
    <span
      className={styles.value}
      title={rounded && exact !== text ? t(m.exact, { value: exact, unit: t(UNIT_LABELS.ha) }) : undefined}
    >
      {text}
      {/* Пробел перед единицей — единственная точка переноса: сам разряд
          числа склеен неразрывными пробелами и не разорвётся. */}
      {unitText ? <> <span className={styles.unit}>{unitText}</span></> : null}
    </span>
  );
}

export function Count({ value }: { value: number | null | undefined }) {
  const { text, missing } = formatCount(value);
  return missing ? (
    <span className={styles.missing}>{missingLabel()}</span>
  ) : (
    <span className={styles.value}>{text}</span>
  );
}

/** Текст, которого может не быть. Пусто — это «неизвестно», не пустая строка. */
export function Text({
  value,
  fallback,
}: {
  value: string | null | undefined;
  fallback?: string;
}) {
  const t = useT();
  if (!value) return <span className={styles.missing}>{fallback ?? t(m.unknown)}</span>;
  return <>{value}</>;
}

/* ------------------------------------------------------------------ */
/* Метки                                                               */
/* ------------------------------------------------------------------ */

export function Badge({
  children,
  tone = "neutral",
  title,
}: {
  children: ReactNode;
  tone?: "neutral" | "source" | "derived" | "demo" | "unknown" | "attention";
  title?: string;
}) {
  const toneClass =
    tone === "source"
      ? styles.badgeSource
      : tone === "derived"
        ? styles.badgeDerived
        : tone === "demo"
          ? styles.badgeDemo
          : tone === "unknown"
            ? styles.badgeUnknown
            : tone === "attention"
              ? styles.badgeAttention
              : "";
  return (
    <span className={`${styles.badge} ${toneClass}`} title={title}>
      {children}
    </span>
  );
}

/**
 * Класс доказательства ровно тот, что выдал сервер. Неизвестный код
 * показывается как «неизвестно» с самим кодом — он не повышается до факта и
 * не сводится к «нет данных».
 */
export function EvidenceBadge({ status }: { status: string | null | undefined }) {
  const evidence = epistemicClass(status);
  const tone = !evidence.known
    ? "unknown"
    : evidence.synthetic
      ? "demo"
      : evidence.code === "official"
        ? "source"
        : "derived";
  return (
    <Badge tone={tone} title={evidence.label}>
      {evidence.known ? evidence.short : tr(UNKNOWN.code, { code: evidence.code || "—" })}
    </Badge>
  );
}

/* ------------------------------------------------------------------ */
/* Карточки и панели                                                   */
/* ------------------------------------------------------------------ */

export function Card({
  title,
  children,
  large = false,
  hero = false,
}: {
  title?: ReactNode;
  children: ReactNode;
  large?: boolean;
  hero?: boolean;
}) {
  const className = hero
    ? styles.hero
    : `${styles.card} ${large ? styles.cardLarge : ""}`;
  return (
    <section className={className}>
      {title ? <div className={styles.cardTitle}>{title}</div> : null}
      {children}
    </section>
  );
}

/** KPI-карточка: подпись → значение/единица → одна нижняя строка состояния. */
export function MetricCard({
  label,
  value,
  bottom,
  hero = false,
  explain,
  explainLabel,
}: {
  label: ReactNode;
  value: ReactNode;
  bottom?: ReactNode;
  hero?: boolean;
  /**
   * Что входит в знаменатель и что в него НЕ входит.
   *
   * Число уровня без знаменателя читается неверно: «74 %» от чего именно —
   * от всех работ, от работ этой кампании, от работ с открытым окном? Раньше
   * это жило в `title` и половине людей не доставалось вовсе.
   */
  explain?: ReactNode;
  /** Предмет объяснения для чтения вслух, если подпись — не строка. */
  explainLabel?: string;
}) {
  const t = useT();
  const title = explain ? (
    <Explained
      label={
        explainLabel ?? (typeof label === "string" ? label : t(m.quantityFallback))
      }
      explain={explain}
    >
      {label}
    </Explained>
  ) : (
    label
  );
  return (
    <Card title={title} hero={hero}>
      <div className={styles.metricValue}>{value}</div>
      {bottom ? <div className={styles.metricBottom}>{bottom}</div> : null}
    </Card>
  );
}

export type DataState =
  | "loading"
  | "empty"
  | "error"
  | "blocked"
  | "missing"
  | "info";

export function DataStatePanel({
  state,
  title,
  children,
  action,
}: {
  state: DataState;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div
      className={`${styles.statePanel} ${state === "error" ? styles.stateError : ""}`}
      role={state === "error" ? "alert" : undefined}
      aria-busy={state === "loading" || undefined}
    >
      <div className={styles.stateTitle}>
        {state === "loading" ? <span className={styles.spinner} aria-hidden /> : null}{" "}
        {title}
      </div>
      {children ? <div className={styles.stateText}>{children}</div> : null}
      {action ? <div className={styles.stateActions}>{action}</div> : null}
    </div>
  );
}

/**
 * Панель неудавшегося запроса — по КОДУ отказа, а не по одному «не загрузилось».
 *
 * Отдельный компонент, потому что различение здесь содержательное, а не
 * оформительское. Отказ по области (`outside_scope`) — это не сбой: сервер
 * ответил, ответ понятен, повторять запрос бессмысленно, а текст ответа уже
 * объясняет человеку, что ему доступно. Подписать такой ответ «Не загрузилось.
 * Повторить» значило бы соврать про причину и предложить бесполезное действие.
 *
 * Текст в теле — ВСЕГДА серверный: правило видимости живёт на сервере, и
 * пересказывать его в интерфейсе значило бы завести второе место, где оно
 * написано.
 *
 * Повтор и НАВИГАЦИЯ разведены разными пропами намеренно. Бесполезен у отказа
 * по области именно повтор; ссылка «вернуться в реестр» бесполезной не
 * становится — наоборот, человек, пришедший по чужой ссылке, без неё остаётся
 * на панели без единого перехода. Один общий `action` прятал бы вместе с
 * кнопкой и выход.
 *
 * Пустой экран здесь невозможен по построению: заголовок есть у каждой ветки.
 */
export function RequestErrorPanel({
  error,
  title,
  notFoundTitle,
  action,
  navigation,
}: {
  /**
   * `null` — данных нет, а отказа не было (запрос ещё не приносил ответа).
   * Тогда объяснять нечего, и панель показывает только заголовок: придумать
   * причину за сервер она не вправе.
   */
  error: ApiError | null;
  /** Заголовок для обычного сбоя. У отказа по области заголовок свой. */
  title: string;
  /** Заголовок для «такого объекта нет», если он у экрана свой. */
  notFoundTitle?: string;
  /** Повтор запроса. У отказа по области не предлагается: он бесполезен. */
  action?: ReactNode;
  /** Переходы (например, назад в реестр). Показываются у ВСЕХ веток. */
  navigation?: ReactNode;
}) {
  const t = useT();
  const both = (
    <>
      {action}
      {navigation}
    </>
  );
  const actions = action || navigation ? both : undefined;
  if (error === null) {
    return <DataStatePanel state="error" title={title} action={actions} />;
  }
  const kind = failureKind(error);
  if (kind === "outside_scope") {
    return (
      <DataStatePanel state="blocked" title={t(m.notYours)} action={navigation}>
        {error.message}
      </DataStatePanel>
    );
  }
  if (kind === "role_required") {
    // Не «нужен вход»: вход уже состоялся. И не «повторить»: повтор вернёт тот
    // же отказ. Чинится это назначением роли, то есть действием другого
    // человека, — и сказать об этом обязан текст сервера, а не заголовок.
    return (
      <DataStatePanel
        state="blocked"
        title={t(m.noRole)}
        action={navigation}
      >
        {error.message}
      </DataStatePanel>
    );
  }
  if (kind === "unauthenticated") {
    return (
      <DataStatePanel state="blocked" title={t(m.signInNeeded)} action={actions}>
        {error.message}
      </DataStatePanel>
    );
  }
  if (kind === "not_found") {
    return (
      <DataStatePanel
        state="empty"
        title={notFoundTitle ?? title}
        action={actions}
      >
        {error.message}
      </DataStatePanel>
    );
  }
  return (
    <DataStatePanel state="error" title={title} action={actions}>
      {error.message}
    </DataStatePanel>
  );
}

/**
 * Плашка «выдача сужена до хозяйств вашего аккаунта» — словами СЕРВЕРА.
 *
 * Ради этого поле `context.scope` и заведено: увидев короткий список, человек
 * обязан узнать из ответа, короток он потому, что работы нет, или потому, что
 * показаны только его хозяйства. Пока плашки нет ни на одном экране, поле
 * существует в контракте, а на экране — нет, и «сужено» неотличимо от «пусто».
 *
 * При районной области плашка не показывается: сужения не было, и объявлять
 * его значило бы предупреждать о том, чего не произошло. `scope` может не
 * прийти вовсе (старый ответ) — тогда молчание честнее выдумки.
 */
export function ScopeNotice({ scope }: { scope?: ScopeOut | null }) {
  const t = useT();
  if (!scope || scope.kind !== "actor") {
    return null;
  }
  return <InlineAlert title={t(m.scopeNarrowed)}>{scope.note}</InlineAlert>;
}

/**
 * Плашка «эта выдача выбранную ось не сужает» — словами СЕРВЕРА.
 *
 * Зеркало :func:`ScopeNotice` для второй половины честности вида. Сервер
 * называет непринятые оси в `context.filters.not_applied` ровно затем, чтобы
 * клиент не принял «проигнорировали» за «сузили»; пока плашки не было, канал
 * существовал в контракте, а на экране — нет: в адресной строке стоял
 * `season=2025`, список был районным, и экран об этом молчал.
 *
 * Показывается ТОЛЬКО про оси, которые человек действительно выбрал
 * (`chosen`): перечислять все непринятые оси подряд значило бы предупреждать о
 * том, чего никто не просил. Текст берётся из ответа, а не пишется здесь:
 * причина принадлежит выдаче, и второй её экземпляр на клиенте разошёлся бы с
 * первым.
 */
export function NotAppliedNotice({
  filters,
  chosen,
}: {
  filters?: { not_applied?: Record<string, string> | null } | null;
  /** Оси, стоящие в адресе: серверное имя оси → выбрана ли она. */
  chosen: Record<string, boolean>;
}) {
  const t = useT();
  const named = filters?.not_applied ?? {};
  const shown = Object.entries(named).filter(([axis]) => chosen[axis]);
  if (shown.length === 0) {
    return null;
  }
  return (
    <InlineAlert title={t(m.axisNotApplied)}>
      {shown.map(([axis, reason]) => (
        <div key={axis}>{reason}</div>
      ))}
    </InlineAlert>
  );
}

export function InlineAlert({
  title,
  children,
}: {
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className={styles.inlineAlert}>
      <div>
        <div className={styles.stateTitle}>{title}</div>
        {children ? <div className={styles.inlineAlertBody}>{children}</div> : null}
      </div>
    </div>
  );
}

export function Button({
  children,
  onClick,
  variant = "secondary",
  type = "button",
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary";
  type?: "button" | "submit";
  disabled?: boolean;
}) {
  return (
    <button
      type={type}
      className={`${styles.button} ${variant === "primary" ? styles.buttonPrimary : ""}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Происхождение                                                       */
/* ------------------------------------------------------------------ */

/** Компактная строка источника: лист, строка, диапазон ячеек. */
export function ProvenanceLine({ source }: { source: SourceRef }) {
  const t = useT();
  if (!source.source_record_id && !source.derivation) {
    return <span className={styles.missing}>{t(m.noProvenance)}</span>;
  }
  return (
    <div className={styles.provenance}>
      {source.sheet_name ? <span>{t(m.sheet, { name: source.sheet_name })}</span> : null}
      {source.row_no ? <span>{t(m.row, { no: source.row_no })}</span> : null}
      {source.cell_range ? <span>{t(m.cells, { range: source.cell_range })}</span> : null}
      {source.derivation ? (
        <span>
          {t(m.derivation, { method: String((source.derivation as Record<string, unknown>).method ?? "—") })}
        </span>
      ) : null}
      {source.source_record_id ? (
        <span className={styles.provenanceId}>#{source.source_record_id}</span>
      ) : null}
    </div>
  );
}

export function DefinitionList({ children }: { children: ReactNode }) {
  return <dl className={styles.definitionList}>{children}</dl>;
}
